// BARRA DE LENGUAJE NATURAL — superficie del shell. El LLM vive en el backend: aquí sólo se envía
// texto y se renderiza texto plano (nunca HTML).
// PC1 (two-step real): quien prepara NUNCA aplica. La solicitud queda PENDIENTE y otro supervisor
// interno la encuentra en «Solicitudes pendientes», la abre desde su propia cuenta y decide.
import { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, X, Send, ShieldCheck, AlertTriangle, Clock, Loader2, Plus, Inbox, ListChecks, RefreshCw, Minus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import SolicitudPanel, { EstadoPill } from "@/components/ia/SolicitudPanel";

const nuevaSesion = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const P = (v) => (v || "").toUpperCase();

export default function IABarra({ estado, abierta, onCerrar, vistaSolicitada = null }) {
  const [vista, setVista] = useState("asistente");
  const [texto, setTexto] = useState("");
  const [cargando, setCargando] = useState(false);
  const [hilo, setHilo] = useState([]);
  const [propuesta, setPropuesta] = useState(null);
  const [aplicando, setAplicando] = useState(false);
  const [resultadoPendiente, setResultadoPendiente] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [mias, setMias] = useState([]);
  const [sel, setSel] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [listando, setListando] = useState(false);
  const sesion = useRef(nuevaSesion());
  const reqRef = useRef(0);
  const finRef = useRef(null);

  useEffect(() => { if (vista === "asistente") finRef.current?.scrollIntoView({ block: "end" }); }, [hilo, propuesta, vista]);

  const cargarLista = useCallback(async (v) => {
    setListando(true);
    try {
      const { data } = await api.get(v === "pendientes" ? "/ia/act/pendientes" : "/ia/act/mias");
      if (v === "pendientes") setPendientes(data.solicitudes || []); else setMias(data.solicitudes || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "No se pudo cargar el listado.");
    } finally { setListando(false); }
  }, []);

  useEffect(() => {
    if (!abierta || vista === "asistente") return;
    setSel(null);
    cargarLista(vista);
  }, [abierta, vista, cargarLista]);

  // Acceso directo desde el contador del shell: abre la bandeja que se pidió.
  useEffect(() => {
    if (abierta && vistaSolicitada) setVista(vistaSolicitada);
  }, [abierta, vistaSolicitada]);

  // ESCAPE minimiza el panel (no descarta conversación, borrador ni propuesta preparada).
  useEffect(() => {
    if (!abierta) return;
    const onKey = (e) => { if (e.key === "Escape") onCerrar?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [abierta, onCerrar]);

  // GC92 · el panel permanece MONTADO mientras la barra esté activa: minimizar no pierde la
  // conversación, el borrador ni la propuesta ya preparada (antes se desmontaba al cerrar).
  if (!estado?.activa) return null;

  // RBAC-I2: quién revisa depende de la cartera. No se le anuncia «supervisor» a la enfermería.
  const revisorTxt = estado.ambito === "institucional"
    ? "otra persona autorizada del centro" : "otro supervisor interno";

  const enviar = async () => {
    const t = texto.trim();
    if (!t || cargando || aplicando) return;
    setTexto("");
    setHilo((h) => [...h, { rol: "yo", texto: t }]);
    setCargando(true);
    const mio = ++reqRef.current;
    setPropuesta(null);
    try {
      const { data } = await api.post("/ia/mensaje", { texto: t, session_id: sesion.current });
      if (mio !== reqRef.current) return;
      setHilo((h) => [...h, { rol: "asistente", texto: data.respuesta || "Sin respuesta." }]);
      if (data.informativo?.mensaje) setHilo((h) => [...h, { rol: "asistente", texto: data.informativo.mensaje }]);
      if (data.propuesta) setPropuesta(data.propuesta);
    } catch (e) {
      if (mio !== reqRef.current) return;
      setHilo((h) => [...h, { rol: "asistente", texto: formatApiErrorDetail(e) || "El asistente no está disponible. La consola manual sigue funcionando." }]);
    } finally { if (mio === reqRef.current) setCargando(false); }
  };

  const abrirDetalle = async (id) => {
    setMotivoRechazo("");
    try {
      const { data } = await api.get(`/ia/act/propuesta/${id}`);
      setSel(data);
      setVista((v) => (v === "asistente" ? "pendientes" : v));
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "No se pudo abrir la solicitud.");
    }
  };

  const confirmarSel = async () => {
    if (!sel || aplicando) return;
    setAplicando(true);
    try {
      const { data } = await api.post("/ia/act/confirmar", { propuesta_id: sel.id, hash: sel.hash });
      if (data.aplicado || data.idempotente) {
        toast.success(data.idempotente ? "Ya estaba aplicada" : "Cambio temporal aplicado");
      } else {
        toast.error(data.mensaje || data.pregunta || "No se aplicó.");
      }
      setSel(null);
      cargarLista("pendientes");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "No se pudo aplicar.");
      cargarLista("pendientes");
    } finally { setAplicando(false); }
  };

  const rechazarSel = async (motivo) => {
    if (!sel || aplicando) return;
    setAplicando(true);
    try {
      await api.post("/ia/act/rechazar", { propuesta_id: sel.id, motivo: motivo ?? motivoRechazo });
      toast.success("Solicitud rechazada");
      setSel(null);
      cargarLista("pendientes");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "No se pudo rechazar.");
    } finally { setAplicando(false); }
  };

  const descartar = async () => {
    if (!propuesta || aplicando) return;
    const pid = propuesta.propuesta_id;
    reqRef.current += 1;
    setPropuesta(null);
    try {
      await api.post("/ia/act/descartar", { propuesta_id: pid });
      setHilo((h) => [...h, { rol: "asistente", texto: "Solicitud descartada. Ya no puede confirmarse." }]);
    } catch (e) {
      setHilo((h) => [...h, { rol: "asistente", texto: formatApiErrorDetail(e) || "No se pudo descartar en el servidor." }]);
    }
  };

  const nuevaConversacion = async () => {
    if (aplicando) return;
    const pid = propuesta?.propuesta_id;
    reqRef.current += 1;
    setHilo([]); setPropuesta(null); setResultadoPendiente(null); setTexto(""); setCargando(false);
    sesion.current = nuevaSesion();
    if (pid) {
      try { await api.post("/ia/act/descartar", { propuesta_id: pid }); }
      catch (e) { toast.error(formatApiErrorDetail(e) || "No se pudo descartar la solicitud anterior."); }
    }
  };

  const tab = (id, label, Icon, testid) => (
    <button data-testid={testid} onClick={() => setVista(id)}
      className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold transition-colors ${
        vista === id ? "bg-[#012472] text-white" : "text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <aside data-testid="ia-barra" aria-label="Asistente Galarya" aria-hidden={!abierta}
      data-abierta={abierta ? "si" : "no"}
      className={`fixed z-40 flex flex-col border-slate-200 bg-white shadow-2xl transition-transform duration-200 dark:border-slate-700 dark:bg-slate-800
        inset-x-0 bottom-0 top-auto max-h-[88dvh] h-[88dvh] rounded-t-2xl border-t pb-[max(0.5rem,env(safe-area-inset-bottom))]
        md:inset-y-0 md:right-0 md:left-auto md:h-auto md:max-h-none md:w-[420px] md:max-w-[calc(100vw-4rem)] md:rounded-none md:border-l md:border-t-0 md:pb-16
        ${abierta ? "translate-y-0" : "pointer-events-none translate-y-full md:translate-x-full md:translate-y-0"}`}>
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-3.5 py-2.5 dark:border-slate-700">
        <p className="inline-flex min-w-0 items-center gap-2 text-xs font-bold text-[#012472] dark:text-slate-100">
          <Sparkles className="h-4 w-4 shrink-0 text-[#01CBE3]" strokeWidth={2} />
          <span className="truncate">Asistente · consulta y prepara</span>
        </p>
        <div className="flex shrink-0 items-center gap-1">
          <button data-testid="ia-barra-minimizar" onClick={onCerrar} title="Minimizar · no pierde la conversación"
            aria-label="Minimizar el asistente"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700">
            <Minus className="h-4 w-4" />
          </button>
          <button data-testid="ia-barra-cerrar" onClick={onCerrar} title="Cerrar"
            aria-label="Cerrar el asistente"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-slate-100 px-2.5 py-1.5 dark:border-slate-700">
        {tab("asistente", "Asistente", Sparkles, "ia-tab-asistente")}
        {estado.puede_revisar && tab("pendientes", "Solicitudes pendientes", Inbox, "ia-tab-pendientes")}
        {tab("mias", "Mis solicitudes", ListChecks, "ia-tab-mias")}
        {vista === "asistente" ? (
          <button data-testid="ia-barra-nueva" onClick={nuevaConversacion} disabled={aplicando}
            title="Nueva conversación limpia el contexto del asistente. No elimina acciones ni registros operativos."
            className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700">
            <Plus className="h-3.5 w-3.5" /> Nueva
          </button>
        ) : (
          <button data-testid="ia-lista-refrescar" onClick={() => cargarLista(vista)} disabled={listando}
            className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700">
            <RefreshCw className={`h-3.5 w-3.5 ${listando ? "animate-spin" : ""}`} /> Actualizar
          </button>
        )}
      </div>

      {vista === "asistente" && (
        <div data-testid="ia-barra-hilo" className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-3.5 py-3">
          {hilo.length === 0 && !propuesta && !resultadoPendiente && (
            <p data-testid="ia-barra-vacio" className="mt-2 text-[11px] leading-relaxed text-slate-400">
              Conversación nueva. Pregunta por el turno, una persona o un periodo; también puedes
              pedir un cambio temporal de prioridad, que {revisorTxt} deberá revisar y confirmar.
              <span className="mt-1.5 block">
                «Nueva» limpia el contexto del asistente. No elimina acciones ni registros operativos.
              </span>
            </p>
          )}
          {hilo.map((m, i) => (
            <div key={i} className={m.rol === "yo" ? "text-right" : ""}>
              <p data-testid={`ia-msg-${m.rol}-${i}`}
                className={`inline-block max-w-[92%] whitespace-pre-line rounded-xl px-3 py-2 text-left text-xs leading-relaxed ${
                  m.rol === "yo" ? "bg-[#012472]/8 text-[#012472] dark:bg-slate-700 dark:text-slate-100"
                    : "bg-slate-50 text-slate-700 dark:bg-slate-900/60 dark:text-slate-200"}`}>
                {m.texto}
              </p>
            </div>
          ))}

          {propuesta && (
            <div data-testid="ia-propuesta" className="rounded-xl border-2 border-[#01CBE3]/60 bg-[#01CBE3]/5 p-3 dark:bg-slate-900/60">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0891A6]">
                <ShieldCheck className="h-3.5 w-3.5" /> Solicitud preparada · nada ha cambiado aún
              </p>
              <dl className="mt-2 space-y-1 text-xs text-slate-700 dark:text-slate-200">
                <div><dt className="inline font-semibold">Persona: </dt><dd data-testid="ia-prop-persona" className="inline">{propuesta.persona_nombre}{propuesta.habitacion ? ` · ${propuesta.habitacion}` : ""}</dd></div>
                <div><dt className="inline font-semibold">Evento: </dt><dd data-testid="ia-prop-evento" className="inline">{propuesta.tipo_evento}</dd></div>
                <div><dt className="inline font-semibold">Prioridad: </dt><dd data-testid="ia-prop-cambio" className="inline">{P(propuesta.prioridad_antes)} → {P(propuesta.prioridad)}</dd></div>
                <div><dt className="inline font-semibold">Vigencia: </dt><dd data-testid="ia-prop-vigencia" className="inline">{propuesta.inicio} → {propuesta.fin} ({propuesta.zona_horaria})</dd></div>
                <div><dt className="inline font-semibold">Duración: </dt><dd data-testid="ia-prop-duracion" className="inline">{propuesta.duracion_txt}</dd></div>
                {propuesta.motivo && <div><dt className="inline font-semibold">Motivo: </dt><dd className="inline">{propuesta.motivo}</dd></div>}
              </dl>
              {(propuesta.avisos || []).map((a, i) => (
                <p key={i} data-testid={`ia-prop-aviso-${i}`} className="mt-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] leading-relaxed text-amber-900 dark:bg-amber-900/25 dark:text-amber-100">{a}</p>
              ))}
              <p className="mt-2 inline-flex items-center gap-1 text-[10px] text-slate-400"><Clock className="h-3 w-3" /> Caduca en {propuesta.ttl_min} min</p>
              <p data-testid="ia-prop-pendiente-revision" className="mt-2 rounded-lg bg-slate-100 px-2 py-1.5 text-[11px] font-semibold leading-relaxed text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                Queda PENDIENTE de revisión: debe confirmarla {revisorTxt.toUpperCase()} desde su
                propia cuenta, en «Solicitudes pendientes». Nadie aprueba su propia solicitud.
              </p>
              <div className="mt-2.5 flex gap-2">
                <button data-testid="ia-prop-descartar" onClick={descartar} disabled={aplicando}
                  className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-700">Descartar mi solicitud</button>
              </div>
            </div>
          )}

          {resultadoPendiente && (
            <div data-testid="ia-resultado-pendiente" className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 dark:bg-amber-900/20">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" /> Resultado pendiente de verificación
              </p>
              <p className="mt-1 text-xs text-amber-900 dark:text-amber-100">{resultadoPendiente.mensaje}</p>
            </div>
          )}
          <div ref={finRef} />
        </div>
      )}

      {vista !== "asistente" && (
        <div data-testid={vista === "pendientes" ? "ia-pendientes-panel" : "ia-mias-panel"}
          className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3.5 py-3">
          {sel ? (
            <div className="rounded-xl border-2 border-[#01CBE3]/60 bg-[#01CBE3]/5 p-3 dark:bg-slate-900/60">
              <button data-testid="ia-revision-volver" onClick={() => setSel(null)}
                className="text-[11px] font-bold text-[#0891A6] hover:underline">← Volver al listado</button>
              <SolicitudPanel s={sel} prefix="ia-rev" aplicando={aplicando}
                onConfirmar={confirmarSel} onRechazar={rechazarSel} revisorTxt={revisorTxt} />
            </div>
          ) : (
            <>
              {listando && <p className="text-[11px] text-slate-400">Cargando…</p>}
              {!listando && (vista === "pendientes" ? pendientes : mias).length === 0 && (
                <p data-testid="ia-lista-vacia" className="mt-2 text-[11px] leading-relaxed text-slate-400">
                  {vista === "pendientes"
                    ? "No hay solicitudes pendientes de otras cuentas para revisar."
                    : "No has preparado ninguna solicitud todavía."}
                </p>
              )}
              {(vista === "pendientes" ? pendientes : mias).map((s) => (
                <button key={s.id} data-testid={`ia-solicitud-item-${s.id}`} onClick={() => abrirDetalle(s.id)}
                  className="block w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition-colors hover:border-[#01CBE3]/70 dark:border-slate-700 dark:bg-slate-900/50">
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-bold text-[#012472] dark:text-slate-100">{s.snapshot?.persona_nombre}</span>
                    <EstadoPill estado={s.estado_display || s.estado} />
                  </span>
                  <span className="mt-0.5 block text-[11px] text-slate-500 dark:text-slate-300">
                    {s.params?.tipo_evento} · {P(s.snapshot?.prioridad_antes)} → {P(s.params?.prioridad)} · {s.snapshot?.duracion_txt}
                  </span>
                  <span className="mt-0.5 block text-[10px] text-slate-400">
                    {vista === "pendientes" ? `Solicitó ${s.actor_email}` : `Revisión: ${s.aprobador_email || "pendiente"}`} · {s.params?.inicio} → {s.params?.fin}
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      )}

      {vista === "asistente" && (
        <div className="flex shrink-0 items-center gap-2 border-t border-slate-100 px-2.5 py-2 dark:border-slate-700">
          <input data-testid="ia-barra-input" value={texto} onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") enviar(); }} disabled={aplicando}
            placeholder="Pregunta o pide algo. Ej: ¿cómo estuvo el turno de anoche?"
            className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-[#01CBE3] disabled:opacity-60 md:py-2 md:text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
          <button data-testid="ia-barra-enviar" onClick={enviar} disabled={cargando || aplicando || !texto.trim()}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#01CBE3] text-white transition-colors hover:bg-[#0891A6] disabled:opacity-50 md:h-8 md:w-8">
            {cargando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      )}
      <p className="shrink-0 px-3.5 pb-2 pt-1.5 text-[10px] leading-snug text-slate-400 mb-12 md:mb-0">
        Consulta lo que tu cuenta ya puede ver. Ningún cambio se aplica sin que {revisorTxt} lo
        confirme.{estado.modelo ? ` · ${estado.proveedor}/${estado.modelo}` : ""}
      </p>
    </aside>
  );
}
